import React, { useEffect, useState } from "react";
import { Plus, ChevronUp, ChevronDown } from "lucide-react";
import CustomDropdown from "../../../Ui_Components/CustomDropdown";
import { DropdownOption } from "../../../Ui_Components/CustomDropdown";
import { ReactComponent as DeleteIcon } from "../../../../assects/svg/client_details/delete.svg";
import {
  getQualificationTypes,
  getSpecializationList,
  getExpertiseTypes,
  getCertificationTypes,
  getLanguageList,
} from "../../../../services/team";
import {
  EducationWorkData,
  EducationWorkErrors,
  QualificationEntry,
  SpecialisationEntry,
  ExpertiseEntry,
  CertificationEntry,
  createId,
} from "../types";

const inputCls = "w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none focus:border-[#0DB5B5] transition-colors bg-white";

const parseYearMonth = (value: string): { years: string; months: string } => {
  const yearMatch = value.match(/(\d+)\s*year/i);
  const monthMatch = value.match(/(\d+)\s*month/i);
  return {
    years: yearMatch?.[1] ?? "",
    months: monthMatch?.[1] ?? "",
  };
};

const formatYearMonth = (years: string, months: string): string => {
  const parts: string[] = [];
  if (years) parts.push(`${years} year`);
  if (months) parts.push(`${months} month`);
  return parts.join(" ");
};

interface YearMonthInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const YearMonthInput: React.FC<YearMonthInputProps> = ({
  value,
  onChange,
  className = "",
}) => {
  const { years, months } = parseYearMonth(value);

  const set = (patch: { years?: string; months?: string }) => {
    const ny = patch.years !== undefined ? patch.years : years;
    const nm = patch.months !== undefined ? patch.months : months;
    onChange(formatYearMonth(ny, nm));
  };

  const adjust = (field: "years" | "months", delta: number) => {
    const current = field === "years" ? years : months;
    const max = field === "years" ? 99 : 11;
    const num = parseInt(current || "0", 10);
    const next = Math.max(0, Math.min(max, num + delta));
    set({ [field]: String(next) });
  };

  return (
    <div className={`flex gap-3 ${className}`}>
      <div className="relative flex-1">
        <input
          type="text"
          inputMode="numeric"
          value={years}
          onChange={(e) =>
            set({ years: e.target.value.replace(/\D/g, "") })
          }
          placeholder="0"
          className={`${inputCls} pr-16`}
        />
        <span className="absolute right-8 top-1/2 -translate-y-1/2 text-sm 3xl:text-base font-inter text-[#9E9E9E] pointer-events-none">
          years
        </span>
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
          <button
            type="button"
            onClick={() => adjust("years", 1)}
            className="h-3.5 w-4 flex items-center justify-center text-[#9E9E9E] hover:text-[#0DB5B5] transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => adjust("years", -1)}
            className="h-3.5 w-4 flex items-center justify-center text-[#9E9E9E] hover:text-[#0DB5B5] transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="relative flex-1">
        <input
          type="text"
          inputMode="numeric"
          value={months}
          onChange={(e) =>
            set({ months: e.target.value.replace(/\D/g, "") })
          }
          placeholder="0"
          className={`${inputCls} pr-20`}
        />
        <span className="absolute right-8 top-1/2 -translate-y-1/2 text-sm 3xl:text-base font-inter text-[#9E9E9E] pointer-events-none">
          months
        </span>
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
          <button
            type="button"
            onClick={() => adjust("months", 1)}
            className="h-3.5 w-4 flex items-center justify-center text-[#9E9E9E] hover:text-[#0DB5B5] transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => adjust("months", -1)}
            className="h-3.5 w-4 flex items-center justify-center text-[#9E9E9E] hover:text-[#0DB5B5] transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

interface EducationWorkStepProps {
  data: EducationWorkData;
  onChange: (data: EducationWorkData) => void;
  errors?: EducationWorkErrors;
}

const errorRingCls = "ring-1 ring-red-400 rounded-md";
const errorInputCls =
  "w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none focus:border-red-500 transition-colors bg-white border-red-400";
const ErrorText: React.FC<{ message?: string }> = ({ message }) =>
  message ? (
    <p className="text-xs 3xl:text-sm text-red-500 font-inter">{message}</p>
  ) : null;

interface DropdownConfig<T> {
  fetcher: () => Promise<any>;
  listKey: string;
  toOption: (item: T) => DropdownOption;
}

const fetchDropdown = async <T,>({
  fetcher,
  listKey,
  toOption,
}: DropdownConfig<T>): Promise<DropdownOption[]> => {
  try {
    const json = await fetcher();
    if (json.success !== "1") return [];
    return (json[listKey] ?? []).map(toOption);
  } catch {
    return [];
  }
};

const EducationWorkStep: React.FC<EducationWorkStepProps> = ({
  data,
  onChange,
  errors = {},
}) => {
  const [qualificationOptions, setQualificationOptions] = useState<DropdownOption[]>([]);
  const [specialisationOptions, setSpecialisationOptions] = useState<DropdownOption[]>([]);
  const [expertiseOptions, setExpertiseOptions] = useState<DropdownOption[]>([]);
  const [certificationOptions, setCertificationOptions] = useState<DropdownOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [quals, specs, exps, certs, langs] = await Promise.all([
        fetchDropdown<{ edu_qua_id: number; edu_qua_name: string }>({
          fetcher: getQualificationTypes,
          listKey: "educations",
          toOption: (q) => ({ value: q.edu_qua_name, label: q.edu_qua_name }),
        }),
        fetchDropdown<{ specialisation_type_id: number; specialisation_name: string }>({
          fetcher: getSpecializationList,
          listKey: "specialisations",
          toOption: (s) => ({
            value: s.specialisation_name,
            label: s.specialisation_name,
          }),
        }),
        fetchDropdown<{ expertise_id: number; expertise: string }>({
          fetcher: getExpertiseTypes,
          listKey: "nutritionist_expertise_data",
          toOption: (e) => ({ value: e.expertise, label: e.expertise }),
        }),
        fetchDropdown<{ certificate_id: number; certification: string }>({
          fetcher: getCertificationTypes,
          listKey: "certifications",
          toOption: (c) => ({ value: c.certification, label: c.certification }),
        }),
        fetchDropdown<{ language_id: number; language_name: string }>({
          fetcher: getLanguageList,
          listKey: "languages",
          toOption: (l) => ({ value: l.language_name, label: l.language_name }),
        }),
      ]);

      if (cancelled) return;
      setQualificationOptions(quals);
      setSpecialisationOptions(specs);
      setExpertiseOptions(exps);
      setCertificationOptions(certs);
      setLanguageOptions(langs);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (patch: Partial<EducationWorkData>) =>
    onChange({ ...data, ...patch });

  /* ── Qualifications ── */
  const addQualification = () =>
    update({
      qualifications: [
        ...data.qualifications,
        { id: createId(), qualification: "", collegeName: "" },
      ],
    });

  const updateQualification = (
    id: string,
    patch: Partial<QualificationEntry>
  ) =>
    update({
      qualifications: data.qualifications.map((q) =>
        q.id === id ? { ...q, ...patch } : q
      ),
    });

  const deleteQualification = (id: string) => {
    const next = data.qualifications.filter((q) => q.id !== id);
    update({
      qualifications:
        next.length > 0
          ? next
          : [{ id: createId(), qualification: "", collegeName: "" }],
    });
  };

  /* ── Specialisations ── */
  const addSpecialisation = () =>
    update({
      specialisations: [
        ...data.specialisations,
        { id: createId(), specialisation: "", yearsInArea: "" },
      ],
    });

  const updateSpecialisation = (
    id: string,
    patch: Partial<SpecialisationEntry>
  ) =>
    update({
      specialisations: data.specialisations.map((s) =>
        s.id === id ? { ...s, ...patch } : s
      ),
    });

  const deleteSpecialisation = (id: string) => {
    const next = data.specialisations.filter((s) => s.id !== id);
    update({
      specialisations:
        next.length > 0
          ? next
          : [{ id: createId(), specialisation: "", yearsInArea: "" }],
    });
  };

  /* ── Expertises ── */
  const addExpertise = () =>
    update({
      expertises: [...data.expertises, { id: createId(), expertise: "" }],
    });

  const updateExpertise = (id: string, patch: Partial<ExpertiseEntry>) =>
    update({
      expertises: data.expertises.map((e) =>
        e.id === id ? { ...e, ...patch } : e
      ),
    });

  const deleteExpertise = (id: string) => {
    const next = data.expertises.filter((e) => e.id !== id);
    update({
      expertises:
        next.length > 0 ? next : [{ id: createId(), expertise: "" }],
    });
  };

  /* ── Certifications ── */
  const addCertification = () =>
    update({
      certifications: [
        ...data.certifications,
        { id: createId(), certification: "" },
      ],
    });

  const updateCertification = (
    id: string,
    patch: Partial<CertificationEntry>
  ) =>
    update({
      certifications: data.certifications.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    });

  const deleteCertification = (id: string) => {
    const next = data.certifications.filter((c) => c.id !== id);
    update({
      certifications:
        next.length > 0 ? next : [{ id: createId(), certification: "" }],
    });
  };

  /* ── Languages ── */
  const updateLanguage = (index: number, value: string) => {
    const langs = [...data.languages] as [string, string, string, string];
    langs[index] = value;
    // Only clear subsequent slots if this slot was cleared — otherwise editing
    // a middle slot would wipe out values the user already entered after it.
    if (value === "") {
      for (let j = index + 1; j < 4; j++) langs[j] = "";
    }
    update({ languages: langs });
  };

  return (
    <div className="space-y-4">
      {/* ── Qualifications card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-3">
        {data.qualifications.map((q, index) => {
          const rowErr = errors.qualifications?.[q.id];
          return (
          <div key={q.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-start">
            <div className="flex flex-col gap-1.5">
              {index === 0 && (
                <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                  Qualification
                </label>
              )}
              <CustomDropdown
                options={qualificationOptions}
                value={q.qualification}
                onChange={(v) => updateQualification(q.id, { qualification: v })}
                placeholder="Select qualification"
                className={rowErr?.qualification ? errorRingCls : ""}
              />
              <ErrorText message={rowErr?.qualification} />
            </div>
            <div className="flex flex-col gap-1.5">
              {index === 0 && (
                <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                  College name
                </label>
              )}
              <input
                type="text"
                value={q.collegeName}
                onChange={(e) => updateQualification(q.id, { collegeName: e.target.value })}
                placeholder="eg. JK College"
                className={rowErr?.collegeName ? errorInputCls : inputCls}
              />
              <ErrorText message={rowErr?.collegeName} />
            </div>
            <div className="flex flex-col gap-1.5">
              {index === 0 && (
                <span
                  aria-hidden
                  className="text-sm 3xl:text-base font-medium font-inter invisible"
                >
                  .
                </span>
              )}
              <button
                type="button"
                onClick={() => deleteQualification(q.id)}
                className="flex items-center justify-center w-9 h-9 rounded-md border border-[#FFD0CD] bg-[#FFF5F5] hover:bg-[#FFE8E6] transition-colors flex-shrink-0"
              >
                <DeleteIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          );
        })}

        <button
          type="button"
          onClick={addQualification}
          className="flex items-center gap-1.5 text-sm 3xl:text-base font-medium font-inter text-[#0DB5B5] hover:text-[#00A9A9] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add qualification
        </button>
      </div>

      {/* ── Work Experience + Specialisation card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
        {/* Work Experience */}
        <div className="flex flex-col gap-1.5 max-w-sm">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Work Experience
          </label>
          <YearMonthInput
            value={data.workExperience}
            onChange={(v) => update({ workExperience: v })}
          />
          <ErrorText message={errors.workExperience} />
        </div>

        {/* Divider */}
        <div className="border-t border-[#F3F4F6]" />

        {/* Specialisation rows */}
        <div className="space-y-3">
          {data.specialisations.map((s, index) => {
            const rowErr = errors.specialisations?.[s.id];
            return (
            <div key={s.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-start">
              <div className="flex flex-col gap-1.5">
                {index === 0 && (
                  <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                    Specialisation
                  </label>
                )}
                <CustomDropdown
                  options={specialisationOptions}
                  value={s.specialisation}
                  onChange={(v) => updateSpecialisation(s.id, { specialisation: v })}
                  placeholder="Select specialisation"
                  className={rowErr?.specialisation ? errorRingCls : ""}
                />
                <ErrorText message={rowErr?.specialisation} />
              </div>
              <div className="flex flex-col gap-1.5">
                {index === 0 && (
                  <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                    Years in this area
                  </label>
                )}
                <YearMonthInput
                  value={s.yearsInArea}
                  onChange={(v) => updateSpecialisation(s.id, { yearsInArea: v })}
                />
                <ErrorText message={rowErr?.yearsInArea} />
              </div>
              <div className="flex flex-col gap-1.5">
                {index === 0 && (
                  <span
                    aria-hidden
                    className="text-sm 3xl:text-base font-medium font-inter invisible"
                  >
                    .
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => deleteSpecialisation(s.id)}
                  className="flex items-center justify-center w-9 h-9 rounded-md border border-[#FFD0CD] bg-[#FFF5F5] hover:bg-[#FFE8E6] transition-colors flex-shrink-0"
                >
                  <DeleteIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })}

          <button
            type="button"
            onClick={addSpecialisation}
            className="flex items-center gap-1.5 text-sm 3xl:text-base font-medium font-inter text-[#0DB5B5] hover:text-[#00A9A9] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add specialisation
          </button>
        </div>
      </div>

      {/* ── Expertise & Certification — two separate cards side by side ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Expertise card */}
        <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-3">
          {data.expertises.map((e, index) => {
            const rowErr = errors.expertises?.[e.id];
            return (
            <div key={e.id} className="flex items-start gap-2">
              <div className="flex flex-col gap-1.5 flex-1">
                {index === 0 && (
                  <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                    Expertise
                  </label>
                )}
                <CustomDropdown
                  options={expertiseOptions}
                  value={e.expertise}
                  onChange={(v) => updateExpertise(e.id, { expertise: v })}
                  placeholder="Select expertise"
                  className={rowErr ? errorRingCls : ""}
                />
                <ErrorText message={rowErr} />
              </div>
              <div className="flex flex-col gap-1.5">
                {index === 0 && (
                  <span
                    aria-hidden
                    className="text-sm 3xl:text-base font-medium font-inter invisible"
                  >
                    .
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => deleteExpertise(e.id)}
                  className="flex items-center justify-center w-9 h-9 rounded-md border border-[#FFD0CD] bg-[#FFF5F5] hover:bg-[#FFE8E6] transition-colors flex-shrink-0"
                >
                  <DeleteIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })}
          <button
            type="button"
            onClick={addExpertise}
            className="flex items-center gap-1.5 text-sm 3xl:text-base font-medium font-inter text-[#0DB5B5] hover:text-[#00A9A9] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add expertise
          </button>
        </div>

        {/* Certification card */}
        <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-3">
          {data.certifications.map((c, index) => {
            const rowErr = errors.certifications?.[c.id];
            return (
            <div key={c.id} className="flex items-start gap-2">
              <div className="flex flex-col gap-1.5 flex-1">
                {index === 0 && (
                  <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                    Certification
                  </label>
                )}
                <CustomDropdown
                  options={certificationOptions}
                  value={c.certification}
                  onChange={(v) => updateCertification(c.id, { certification: v })}
                  placeholder="Select certification"
                  className={rowErr ? errorRingCls : ""}
                />
                <ErrorText message={rowErr} />
              </div>
              <div className="flex flex-col gap-1.5">
                {index === 0 && (
                  <span
                    aria-hidden
                    className="text-sm 3xl:text-base font-medium font-inter invisible"
                  >
                    .
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => deleteCertification(c.id)}
                  className="flex items-center justify-center w-9 h-9 rounded-md border border-[#FFD0CD] bg-[#FFF5F5] hover:bg-[#FFE8E6] transition-colors flex-shrink-0"
                >
                  <DeleteIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })}
          <button
            type="button"
            onClick={addCertification}
            className="flex items-center gap-1.5 text-sm 3xl:text-base font-medium font-inter text-[#0DB5B5] hover:text-[#00A9A9] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add certification
          </button>
        </div>

      </div>{/* end expertise & certification grid */}

      {/* ── Languages card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Language known
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {([0, 1, 2, 3] as const).map((i) => {
              // exclude languages already chosen in other slots
              const otherSelected = data.languages.filter(
                (lang, idx) => idx !== i && lang !== ""
              );
              const available = languageOptions.filter(
                (opt) => !otherSelected.includes(opt.value)
              );
              // sequential: slot i is enabled only when all previous slots are filled
              const isDisabled = i > 0 && data.languages[i - 1] === "";
              const showError = i === 0 && !!errors.languages;

              return (
                <CustomDropdown
                  key={i}
                  options={available}
                  value={data.languages[i]}
                  onChange={(v) => updateLanguage(i, v)}
                  placeholder={`Language ${i + 1}`}
                  disabled={isDisabled}
                  className={showError ? errorRingCls : ""}
                />
              );
            })}
          </div>
          <ErrorText message={errors.languages} />
        </div>
      </div>
    </div>
  );
};

export default EducationWorkStep;
